import React, {
  CSSProperties,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useCallbackRef from 'hooks/useCallbackRef';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import useMatchMedia from 'hooks/useMatchMedia';
import ArrowrightIcon from 'icons/Arrowright.icon';
import ArrowleftIcon from 'icons/Arrowleft.icon';

import './DragScroll.styles.scss';

type Props = {
  adjustToMobile?: boolean;
  arrowScrollAmount?: number;
  style?: CSSProperties;
  className?: string;
  children?: React.ReactNode;
  wrapperClassname?: string;
};

type PointerEv =
  | React.MouseEvent<HTMLDivElement, MouseEvent>
  | React.TouchEvent<HTMLDivElement>;

const TIME_CONSTANT = 325;

const DragScroll: React.ForwardRefRenderFunction<HTMLDivElement, Props> = (
  props,
  ref,
) => {
  const {
    children,
    style,
    arrowScrollAmount,
    className,
    wrapperClassname,
    adjustToMobile = false,
  } = props;
  const [isDragging, setIsDragging] = useState(false);
  const [translateValue, setTranslateValue] = useState(0);
  const [container, containerRef] = useCallbackRef<HTMLDivElement>();
  const trackingInterval = useRef<any>(null);
  const trackingTranslate = useRef(translateValue);
  const trackingXOffset = useRef(translateValue);
  const trackedVelocity = useRef(0);
  const trackedAmplitude = useRef(0);
  const trackedScrollTarget = useRef(0);
  const timestamp = useRef(0);
  const xStart = useRef(0);
  const dragscrollElRef = useRef<HTMLDivElement>(null);

  const isPhablet = useMatchMedia('isPhablet');

  const classes = classNames([className, 'dragscroll']);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkShowArrows = useCallback(() => {
    const el = container;
    if (!el) return;
    setCanScrollRight(translateValue < el.scrollWidth - el.clientWidth);
    setCanScrollLeft(translateValue > 0);
  }, [container, translateValue]);

  useEffect(() => {
    window.addEventListener('resize', checkShowArrows);

    return () => {
      window.removeEventListener('resize', checkShowArrows);
    };
  }, [checkShowArrows]);

  useEffect(() => {
    checkShowArrows();
  }, [checkShowArrows, translateValue]);

  const scroll = useCallback(
    (offset: number) => {
      const minScroll = 0;
      const maxScroll = container?.scrollWidth - container?.clientWidth;

      let corrected = false;
      if (offset > maxScroll) {
        trackingTranslate.current = maxScroll;
        corrected = true;
      } else if (offset < minScroll) {
        trackingTranslate.current = minScroll;
        corrected = true;
      } else {
        trackingTranslate.current = offset;
      }
      setTranslateValue(trackingTranslate.current);
      return !corrected;
    },
    [container],
  );

  const autoScroll = useCallback(() => {
    const { current: amplitude } = trackedAmplitude;
    if (amplitude) {
      const elapsed = Date.now() - timestamp.current;
      const delta = -amplitude * Math.exp(-elapsed / TIME_CONSTANT);
      if (delta > 0.5 || delta < -0.5) {
        if (!scroll(trackedScrollTarget.current + delta)) return;
        requestAnimationFrame(autoScroll);
      } else {
        scroll(trackedScrollTarget.current);
      }
    }
  }, [scroll]);

  const scrollHorizontal = useCallback(
    (times: number, isLeft = false) => {
      const width = arrowScrollAmount || 0.1 * container.scrollWidth;

      const velocity = isLeft ? -times * width : times * width;
      trackedAmplitude.current = 0.8 * velocity;
      trackedScrollTarget.current = Math.round(
        trackingTranslate.current + trackedAmplitude.current,
      );
      timestamp.current = Date.now();
      requestAnimationFrame(autoScroll);
    },
    [arrowScrollAmount, container, autoScroll],
  );

  const calculateXStart = useCallback((e: PointerEv) => {
    // touch event
    if (
      (e as React.TouchEvent).targetTouches &&
      (e as React.TouchEvent).targetTouches.length >= 1
    ) {
      return (e as React.TouchEvent).targetTouches[0].clientX;
    }

    // mouse event
    else return (e as React.MouseEvent).clientX;
  }, []);

  const track = useCallback(() => {
    const now = Date.now();
    const elapsed = now - timestamp.current;
    timestamp.current = now;
    const delta = trackingTranslate.current - trackingXOffset.current;
    trackingXOffset.current = trackingTranslate.current;

    const v = (1000 * delta) / (1 + elapsed);
    trackedVelocity.current = 0.8 * v + 0.2 * trackedVelocity.current;
  }, []);

  const tap = useCallback(
    (event: PointerEv) => {
      setIsDragging(true);
      xStart.current = calculateXStart(event);

      trackedVelocity.current = trackedAmplitude.current = 0;
      trackingXOffset.current = trackingTranslate.current;
      timestamp.current = Date.now();
      clearInterval(trackingInterval.current);
      trackingInterval.current = setInterval(track, 100);

      event.stopPropagation();
      return false;
    },
    [calculateXStart, track],
  );

  const drag = useCallback(
    (event: PointerEv) => {
      if (!isDragging) return;

      const currentX = calculateXStart(event);
      const delta = xStart.current - currentX;
      if (delta > 2 || delta < -2) {
        xStart.current = currentX;
        scroll(trackingTranslate.current + delta);
      }
      event.stopPropagation();
      return false;
    },
    [isDragging, calculateXStart, scroll],
  );

  const release = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    clearInterval(trackingInterval.current);
    const { current: velocity } = trackedVelocity;
    if (velocity > 10 || velocity < -10) {
      window.addEventListener('click', (ev) => ev.stopPropagation(), {
        capture: true,
        once: true,
      });
      trackedAmplitude.current = 0.8 * velocity;
      trackedScrollTarget.current = Math.round(
        trackingTranslate.current + trackedAmplitude.current,
      );

      timestamp.current = Date.now();
      requestAnimationFrame(autoScroll);
    }

    return false;
  }, [isDragging, autoScroll]);

  useEffect(() => {
    if (!container) return;
    const config = { childList: true };

    const callback: MutationCallback = function (mutationsList) {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          checkShowArrows();
        }
      }
    };

    const observer = new MutationObserver(callback);
    observer.observe(container, config);

    return () => {
      observer.disconnect();
    };
  }, [container, checkShowArrows]);

  const ableToScroll = useMemo(
    () => canScrollLeft || canScrollRight,
    [canScrollLeft, canScrollRight],
  );

  useEffect(() => {
    if (adjustToMobile && isPhablet) return;
    if (!isDragging) return;
    window.addEventListener('mouseup', release);
    window.addEventListener('touchend', release);

    return () => {
      window.removeEventListener('mouseup', release);
      window.removeEventListener('touchend', release);
    };
  }, [release, isDragging, adjustToMobile, isPhablet]);

  const scrollingDirection = useRef<'x' | 'y' | 'none'>('none');
  const scrollingTimeout = useRef<NodeJS.Timeout | number>();

  useEffect(() => {
    if (adjustToMobile && isPhablet) {
      return;
    }

    const { current: dragscrollEl } = dragscrollElRef;
    const onWheel = (ev: WheelEvent) => {
      clearInterval(scrollingTimeout.current as number);

      scrollingTimeout.current = setTimeout(() => {
        scrollingDirection.current = 'none';
      }, 50);

      if (scrollingDirection.current === 'none') {
        if (Math.abs(ev.deltaX) > Math.abs(ev.deltaY)) {
          scrollingDirection.current = 'x';
        } else {
          scrollingDirection.current = 'y';
        }
      }

      if (scrollingDirection.current !== 'x') return;

      ev.preventDefault();
      if (isDragging) setIsDragging(false);
      trackedVelocity.current = trackedAmplitude.current = 0;
      scroll(trackingTranslate.current + ev.deltaX);
    };

    dragscrollEl.addEventListener('wheel', onWheel);

    return () => {
      dragscrollEl.removeEventListener('wheel', onWheel);
    };
  }, [adjustToMobile, isDragging, isPhablet, scroll]);

  useEffect(() => {
    if (!adjustToMobile) return;

    if (!isPhablet) {
      dragscrollElRef.current.scrollTo(0, 0);
    }
  }, [adjustToMobile, isPhablet]);

  return (
    <div
      ref={dragscrollElRef}
      className={classNames('dragscroll-wrapper', wrapperClassname, {
        'mobile-adjusted': adjustToMobile,
      })}
      {...(!(adjustToMobile && isPhablet) && {
        onMouseDown: tap,
        onMouseMove: drag,
        onTouchStart: tap,
        onTouchMove: drag,
      })}
      style={{
        ...(ableToScroll && { cursor: isDragging ? 'grabbing' : 'grab' }),
      }}
    >
      {canScrollLeft && (
        <Button
          onClick={() => scrollHorizontal(3, true)}
          className="dragscroll__arrow dragscroll__arrow--left"
          variant="icon"
          aria-label="Scroll to left."
        >
          <ArrowleftIcon />
        </Button>
      )}
      {canScrollRight && (
        <Button
          onClick={() => scrollHorizontal(3)}
          className="dragscroll__arrow dragscroll__arrow--right"
          variant="icon"
          aria-label="Scroll to right."
        >
          <ArrowrightIcon />
        </Button>
      )}
      <div
        ref={(r) => {
          containerRef(r);
          (ref as (ref: any) => void)?.(r);
        }}
        style={{
          ...style,
          transform: `translate3d(-${
            adjustToMobile && isPhablet ? 0 : translateValue
          }px, 0, 0)`,
        }}
        className={classes}
      >
        {children}
      </div>
    </div>
  );
};

export default forwardRef(DragScroll);
